import React, { useState } from "react";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { db } from "../firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { useTranslation } from "../useTranslation";
import one from "../assets/partnership/one.png";
import two from "../assets/partnership/two.jpeg";
import three from "../assets/partnership/three.jpeg";
import four from "../assets/partnership/four.jpeg";
import five from "../assets/partnership/five.webp";
import six from "../assets/partnership/six.jpg";
import seven from "../assets/partnership/seven.jpg";

const Partnerships = () => {
  const { t } = useTranslation(); // Add this line
  const [status, setStatus] = useState({ state: "idle" });
  const [form, setForm] = useState({
    email: "",
    fullName: "",
    partnerType: "",
    message: "",
    consent: false,
  });

  // Basic validation helpers
  const emailOk = /\S+@\S+\.\S+/.test(form.email);
  const nameOk = form.fullName.trim().length >= 2;
  const partnerTypeOk = form.partnerType.length > 0;
  const consentOk = form.consent;

  const disabled = !emailOk || !nameOk || !partnerTypeOk || !consentOk || status.state === "loading";

  const PARTNER_TYPES = [
    t('partnerships.affiliateProgram'),
    t('partnerships.eventPartnership'),
    t('partnerships.travelPartnership'),
    t('partnerships.contentPartnership'),
    t('partnerships.sportsPartnership'),
    t('partnerships.musicCulturePartnership'),
    t('partnerships.athleteCollaboration'),
    t('partnerships.other')
  ];

  const onSubmit = async (e) => {
    e.preventDefault();
    if (disabled) return;
    try {
      setStatus({ state: "loading" });
      const payload = {
        ...form,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        createdAt: serverTimestamp(),
      };
      await addDoc(collection(db, "partnerships"), payload);
      setStatus({ state: "success" });
      setForm({
        email: "",
        fullName: "",
        partnerType: "",
        message: "",
        consent: false,
      });
    } catch (err) {
      console.error("Partnership submit error", err);
      setStatus({ state: "error", message: err?.message || "Something went wrong" });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-purple-500 text-sm font-semibold uppercase tracking-wide mb-4">
            {t('partnerships.headerLabel')}
          </p>
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('partnerships.title')}
          </h1>
          <p className="text-lg text-gray-600 mb-12">
            {t('partnerships.subtitle')}
          </p>
          
          {/* Affiliate Program Section */}
          <div className="flex flex-col lg:flex-row items-center gap-12 mb-20">
            <div className="lg:w-1/2 text-left">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                <span className="text-purple-500">{t('partnerships.join')}</span> {t('partnerships.airvoyAffiliate')}
              </h2>
              <p className="text-base text-gray-600 mb-8">
                {t('partnerships.affiliateDescription')}
              </p>
              <div className="flex gap-4">
                <button 
                  onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-purple-500 text-white px-8 py-3 rounded-full font-semibold hover:bg-purple-600 transition-colors"
                >
                  {t('partnerships.joinNow')}
                </button>
                <button 
                  onClick={() => document.getElementById('partnership-types').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-gray-800 text-white px-8 py-3 rounded-full font-semibold hover:bg-gray-900 transition-colors flex items-center gap-2"
                >
                  {t('partnerships.learnMore')} →
                </button>
              </div>
            </div>
            <div className="lg:w-1/2">
              <div className="relative">
                <img 
                  src={one} 
                  alt={t('partnerships.partnershipImageAlt')} 
                  className="w-full h-auto rounded-2xl shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Partnership Types Grid */}
      <div id="partnership-types" className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            {/* Event Partnerships */}
            <div>
              <img src={two} alt={t('partnerships.eventPartnership')} className="w-full h-48 object-cover rounded-2xl mb-6" />
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('partnerships.eventPartnerships')}</h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('partnerships.eventDescription1')}
              </p>
              <p className="text-sm text-gray-600">
                {t('partnerships.eventDescription2')}
              </p>
            </div>

            {/* Travel Partnerships */}
            <div>
              <img src={three} alt={t('partnerships.travelPartnership')} className="w-full h-48 object-cover rounded-2xl mb-6" />
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('partnerships.travelPartnerships')}</h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('partnerships.travelDescription1')}
              </p>
              <p className="text-sm text-gray-600">
                {t('partnerships.travelDescription2')}
              </p>
            </div>

            {/* Content Partnerships */}
            <div>
              <img src={four} alt={t('partnerships.contentPartnership')} className="w-full h-48 object-cover rounded-2xl mb-6" />
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('partnerships.contentPartnerships')}</h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('partnerships.contentDescription1')}
              </p>
              <p className="text-sm text-gray-600">
                {t('partnerships.contentDescription2')}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {/* Sports Partnerships */}
            <div>
              <img src={five} alt={t('partnerships.sportsPartnership')} className="w-full h-48 object-cover rounded-2xl mb-6" />
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('partnerships.sportsPartnerships')}</h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('partnerships.sportsDescription1')}
              </p>
              <p className="text-sm text-gray-600">
                {t('partnerships.sportsDescription2')}
              </p>
            </div>

            {/* Music & Culture Partnerships */}
            <div>
              <img src={six} alt={t('partnerships.musicCulturePartnership')} className="w-full h-48 object-cover rounded-2xl mb-6" />
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('partnerships.musicCulturePartnerships')}</h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('partnerships.musicDescription1')}
              </p>
              <p className="text-sm text-gray-600">
                {t('partnerships.musicDescription2')}
              </p>
            </div>

            {/* Athlete Collaborations */}
            <div>
              <img src={seven} alt={t('partnerships.athleteCollaboration')} className="w-full h-48 object-cover rounded-2xl mb-6" />
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('partnerships.athleteCollaborations')}</h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('partnerships.athleteDescription1')}
              </p>
              <p className="text-sm text-gray-600">
                {t('partnerships.athleteDescription2')}
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <button 
              onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
              className="bg-gray-800 text-white px-8 py-4 rounded-full font-semibold hover:bg-gray-900 transition-colors text-base"
            >
              {t('partnerships.letsTalk')}
            </button>
          </div>
        </div>
      </div>

      {/* Contact Form */}
      <div id="contact-form" className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <div className="bg-gray-50 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">{t('partnerships.getInTouch')}</h2>
            
            {status.state === "success" && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-800">
                <CheckCircle2 className="w-5 h-5" />
                {t('partnerships.successMessage')}
              </div>
            )}

            {status.state === "error" && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
                <AlertTriangle className="w-5 h-5" />
                {status.message || t('partnerships.errorMessage')}
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-6">
              <div>
                <input
                  type="email"
                  placeholder={t('partnerships.emailPlaceholder')}
                  value={form.email}
                  onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <input
                  type="text"
                  placeholder={t('partnerships.fullNamePlaceholder')}
                  value={form.fullName}
                  onChange={(e) => setForm(f => ({ ...f, fullName: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              <div className="relative">
                <select
                  value={form.partnerType}
                  onChange={(e) => setForm(f => ({ ...f, partnerType: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white text-gray-700"
                  required
                >
                  <option value="">{t('partnerships.partnerTypePlaceholder')}</option>
                  {PARTNER_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              <div>
                <textarea
                  placeholder={t('partnerships.messagePlaceholder')}
                  value={form.message}
                  onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="consent"
                  checked={form.consent}
                  onChange={(e) => setForm(f => ({ ...f, consent: e.target.checked }))}
                  className="w-4 h-4 text-purple-500 focus:ring-purple-500 border-gray-300 rounded"
                  required
                />
                <label htmlFor="consent" className="text-sm text-gray-600">
                  {t('partnerships.privacyConsent')}{" "}
                  <a target="_blank" href="/privacy" className="text-purple-500 hover:underline">
                    {t('partnerships.privacyPolicy')}
                  </a>
                  .
                </label>
              </div>

              <button
                type="submit"
                disabled={disabled}
                className="w-full bg-purple-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {status.state === "loading" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('partnerships.sending')}
                  </>
                ) : (
                  t('partnerships.send')
                )}
              </button>
            </form>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Partnerships;